/**
 * GameBoard - メインゲームボードコンポーネント（スケルトン版）
 *
 * FRONTEND.md Step.1 準拠:
 * - 標準HTMLタグのみ使用（div, button, span等）
 * - className空（Lovableが後で付与）
 * - Propsのみで受け取り、内部状態を持たない
 *
 * @see FRONTEND.md
 * @see src/types/ui-interfaces.ts
 */

import { ReactNode, useState, useCallback, useRef, useEffect } from 'react';
import type { Hex, HexCoordinate, TileType, GoodsDisplay, GoodsColor } from '../types';
import type { PlayerStatsProps, GoodsDisplayBoardProps } from '../types/ui-interfaces';
import { useTranslations } from '../i18n';
import { getI18nMapKeyByMapName } from '../config/gameRegistry';
import { useIsMobile } from '../hooks/useIsMobile';

// 子コンポーネント（Lovable スタイル適用済み）
import { MapDisplay } from './map/MapDisplay';
import { GoodsDisplayBoard } from './common/GoodsDisplayBoard';
import { SettingsModal } from './SettingsModal';

/**
 * GameBoardのProps定義
 */
export interface GameBoardProps {
  /** ヘックスデータ */
  hexes: Hex[];
  /** 商品ディスプレイデータ */
  goodsDisplay: GoodsDisplay;
  /** フェーズ固有のコンテンツ */
  phaseContent: ReactNode;
  /** ヘックスクリック時のコールバック */
  onHexClick?: (hex: Hex) => void;
  /** ヘックス右クリック時のコールバック（反時計回り回転用） */
  onHexRightClick?: (hex: Hex) => void;
  /** 仮配置タイル情報 */
  temporaryTilePlacement?: {
    hexCoordinate: HexCoordinate;
    tileType: TileType;
    rotation: 0 | 1 | 2 | 3 | 4 | 5;
    validationError?: string | null;
    validationErrorCode?: string;
    cost?: number;
  } | null;
  /** 商品ディスプレイ表示状態 */
  showGoodsDisplay: boolean;
  /** 商品ディスプレイ表示切替コールバック */
  onSetShowGoodsDisplay: (show: boolean) => void;
  /** Production モード設定（Phase 9 で PRODUCTION 選択時） */
  productionMode?: GoodsDisplayBoardProps['productionMode'];
  /** プレイヤーステータスProps */
  playerStatsProps: PlayerStatsProps;
  /** 現在のフェーズ名（オプション） */
  phaseName?: string;
  /** 現在のターン（オプション） */
  currentTurn?: number;
  /** ハイライト対象の商品（Ph.5 輸送選択時） */
  highlightedGoods?: { cityId: string; color: GoodsColor } | null;
  /** ハイライト対象の都市ID配列（From/To） */
  highlightedCityIds?: string[] | null;
  /** ハイライト時に表示するIncome上昇値 */
  highlightIncome?: number | null;
  /** ハイライト対象の経路上のヘクス座標（線路ハイライト用） */
  highlightedTrackHexCoords?: Array<{ q: number; r: number }> | null;
  /** マップ名（タイトル表示用） */
  mapName?: string;
  /** フェリーリンク（離れたヘクス間の仮想リンク） */
  ferryLinks?: Array<{
    id: string;
    from: { q: number; r: number };
    to: { q: number; r: number };
    cost: number;
    /** 所有プレイヤーの色（未敷設の場合は null） */
    ownerColor: string | null;
  }>;
  /** 隣接リンク（隣接ヘクス間の特殊コストリンク） */
  adjacentLinks?: Array<{
    id: string;
    from: { q: number; r: number };
    to: { q: number; r: number };
    cost: number;
    /** 所有プレイヤーの色（未敷設の場合は null） */
    ownerColor: string | null;
  }>;
  /** 特殊リンク（フェリー/隣接）クリック時のコールバック */
  onLinkClick?: (linkType: 'ferry' | 'adjacent', linkId: string) => void;
  /** 商品ディスプレイボタンを表示するか（デフォルト: true） */
  showGoodsDisplayButton?: boolean;
  /** 特殊リンク仮配置情報（バルーン表示用） */
  temporaryLinkPlacement?: {
    linkType: 'ferry' | 'adjacent';
    linkId: string;
    cost: number;
    validationError: string | null;
    validationErrorCode?: string;
  } | null;
  /** 強調表示する特殊リンクID（Ph.5 経路表示用） */
  highlightedSpecialLinkIds?: Array<{
    linkType: 'ferry' | 'adjacent';
    linkId: string;
  }> | null;
  /** Seattle Metro 専用オーバーレイ */
  seattleOverlay?: {
    diceBadges: Array<{
      locationId: string;
      diceValue: number;
      coordinate: { q: number; r: number };
    }>;
    deliveredCubes: number;
    warnings: Array<{
      diceValue: number;
      remainingSlots: number;
      centerCoordinate: { q: number; r: number };
    }>;
  } | null;
  /** 配置エラー表示（Phase 9 商品配置で満杯の City/Town をクリックした場合） */
  placementError?: {
    coordinate: { q: number; r: number };
    message: string;
  } | null;
  /** マップ表示の初期向き（デフォルト: 'portrait'） */
  initialOrientation?: 'portrait' | 'landscape';
  /** ルール説明を表示するコールバック */
  onShowRules?: () => void;
  /** ゲームをリセットするコールバック */
  onResetGame?: () => void;
  /** ターン開始に戻すコールバック */
  onUndoToTurnStart?: () => void;
  /** ターン開始に戻すが可能かどうか */
  canUndoToTurnStart?: boolean;
}

/**
 * ゲームボード全体のレイアウトコンポーネント
 *
 * 構造:
 * - 左側: メインマップエリア（2/3幅）
 * - 右側: サイドバー（1/3幅）
 *   - Phase表示
 *   - プレイヤーステータス
 *   - フェーズ固有コンテンツ
 *   - デバッグ情報
 */
export function GameBoard({
  hexes,
  goodsDisplay,
  phaseContent,
  onHexClick,
  onHexRightClick,
  temporaryTilePlacement,
  showGoodsDisplay,
  onSetShowGoodsDisplay,
  productionMode,
  playerStatsProps,
  phaseName = 'unknown',
  currentTurn = 1,
  highlightedGoods,
  highlightedCityIds,
  highlightIncome,
  highlightedTrackHexCoords,
  mapName,
  ferryLinks = [],
  adjacentLinks = [],
  onLinkClick,
  showGoodsDisplayButton = true,
  temporaryLinkPlacement,
  highlightedSpecialLinkIds,
  seattleOverlay,
  placementError,
  initialOrientation = 'portrait',
  onShowRules,
  onResetGame,
  onUndoToTurnStart,
  canUndoToTurnStart,
}: GameBoardProps) {
  // i18n
  const t = useTranslations();

  // スマホ判定
  const isMobile = useIsMobile();

  // ============================================================================
  // マップ制御状態
  // ============================================================================
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>(initialOrientation);
  const [zoomLevel, setZoomLevel] = useState(1.0);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [showSettings, setShowSettings] = useState(false);
  const mapContainerRef = useRef<HTMLDivElement>(null);

  // 回転切り替え
  const handleToggleOrientation = useCallback(() => {
    setOrientation((prev) => (prev === 'portrait' ? 'landscape' : 'portrait'));
  }, []);

  // ズームイン
  const handleZoomIn = useCallback(() => {
    setZoomLevel((prev) => Math.min(prev + 0.2, 3.0));
  }, []);

  // ズームアウト
  const handleZoomOut = useCallback(() => {
    setZoomLevel((prev) => Math.max(prev - 0.2, 0.4));
  }, []);

  // ドラッグ開始
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button === 0) { // 左クリックのみ
      setIsDragging(true);
      setDragStart({ x: e.clientX - panPosition.x, y: e.clientY - panPosition.y });
    }
  }, [panPosition]);

  // ドラッグ中
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging) {
      setPanPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  }, [isDragging, dragStart]);

  // ドラッグ終了
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // マウスがコンテナから出た時もドラッグ終了
  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  // ホイールでズーム
  useEffect(() => {
    const container = mapContainerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setZoomLevel((prev) => Math.max(0.4, Math.min(3.0, prev + delta)));
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, []);

  // ============================================================================
  // タッチ操作（スマホ用：2本指でパンのみ、ピンチズームは無効）
  // ============================================================================

  // 2点の中心座標を計算
  const getTouchCenter = useCallback((touches: React.TouchList): { x: number; y: number } => {
    return {
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2,
    };
  }, []);

  // タッチ開始
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      // 2本指: パン開始
      setIsDragging(true);
      const center = getTouchCenter(e.touches);
      setDragStart({
        x: center.x - panPosition.x,
        y: center.y - panPosition.y,
      });
    }
  }, [panPosition, getTouchCenter]);

  // タッチ移動
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2 && isDragging) {
      e.preventDefault(); // スクロールを防止
      // 2本指: パン
      const center = getTouchCenter(e.touches);
      setPanPosition({
        x: center.x - dragStart.x,
        y: center.y - dragStart.y,
      });
    }
  }, [isDragging, dragStart, getTouchCenter]);

  // タッチ終了
  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Phase名の表示テキスト
  const getPhaseDisplayName = (phase: string): string => {
    const phaseNames: Record<string, string> = {
      issueShares: t.phases.issueShares,
      selectActions: t.phases.selectActions,
      buildTrack: t.phases.buildTrack,
      moveGoods: t.phases.moveGoods,
      collectIncome: t.phases.collectIncome,
      payExpenses: t.phases.payExpenses,
      incomeReduction: t.phases.incomeReduction,
      refillGoods: t.phases.refillGoods,
      advanceTurn: t.phases.advanceTurn,
      Completed: t.phases.completed,
    };
    return phaseNames[phase] || phase;
  };

  // マップ名の短縮版（スマホ用）
  const shortMapName = mapName ? t.mapSelection.maps[getI18nMapKeyByMapName(mapName)].name : '';

  return (
    <div className={`flex h-screen bg-background font-body ${isMobile ? 'p-0 gap-0.5' : 'p-2 gap-2 md:p-4 md:gap-4'}`}>
      {/* 左側: メインマップエリア */}
      <div className="flex-1 md:flex-[3] flex flex-col min-w-0">
        {/* ヘッダー: スマホでは統合ヘッダー、PCではタイトルのみ */}
        {isMobile ? (
          // スマホ: タイトル + PlayerInfo 統合ヘッダー（1行）
          <div className="flex items-center justify-between bg-card px-2 py-0.5 border-b border-border text-[12px]">
            <div className="flex items-center gap-3">
              <span className="font-heading font-semibold truncate max-w-[120px] text-[20px]">
                {shortMapName}
              </span>
              <span className="text-muted-foreground">|</span>
              <span className={playerStatsProps.player.cash < 0 ? 'text-destructive' : ''}>
                {t.stats.cashShort}${playerStatsProps.player.cash}
              </span>
              <span className={playerStatsProps.player.incomeLevel <= 0 ? 'text-destructive' : ''}>
                {t.stats.incomeShort}+{playerStatsProps.player.incomeLevel}
              </span>
              <span className="text-destructive">
                {t.stats.payShort}-${playerStatsProps.expenses ?? 0}
              </span>
              <span>{t.stats.issueShort}{playerStatsProps.player.sharesIssued}</span>
              <span>🚂{playerStatsProps.player.locomotiveLevel}</span>
              {playerStatsProps.cargoTransportLevel !== undefined && (
                <span>{t.stats.cargoShort}{playerStatsProps.cargoTransportLevel}</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">
                T{playerStatsProps.maxTurns === -1 ? currentTurn : `${currentTurn}/${playerStatsProps.maxTurns}`}
              </span>
              {showGoodsDisplayButton && (
                <button
                  className="btn-secondary text-[14px] px-1.5 py-0.5"
                  onClick={() => onSetShowGoodsDisplay(true)}
                >
                  📦
                </button>
              )}
              <button
                className="btn-ghost text-[16px] px-1 py-0.5"
                onClick={() => setShowSettings(true)}
                aria-label={t.settings.title}
              >
                ⚙️
              </button>
            </div>
          </div>
        ) : (
          // PC: 従来のタイトルバー
          <div className="mb-2 md:mb-4 text-center">
            <h1 className="font-heading text-lg md:text-2xl font-semibold text-foreground">
              {t.game.titlePrefix}{mapName ? ` - ${shortMapName}` : ''}
            </h1>
          </div>
        )}

        {/* マップ表示 */}
        <div
          ref={mapContainerRef}
          className={`flex-1 bg-card overflow-hidden ${isMobile ? '' : 'rounded-lg shadow-subtle border border-border'} ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <MapDisplay
            hexes={hexes}
            onHexClick={onHexClick}
            onHexRightClick={onHexRightClick}
            temporaryTilePlacement={temporaryTilePlacement}
            highlightedGoods={highlightedGoods}
            highlightedCityIds={highlightedCityIds}
            highlightIncome={highlightIncome}
            highlightedTrackHexCoords={highlightedTrackHexCoords}
            orientation={orientation}
            onToggleOrientation={handleToggleOrientation}
            zoomLevel={zoomLevel}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            panPosition={panPosition}
            onPanChange={setPanPosition}
            ferryLinks={ferryLinks}
            adjacentLinks={adjacentLinks}
            onLinkClick={onLinkClick}
            temporaryLinkPlacement={temporaryLinkPlacement}
            highlightedSpecialLinkIds={highlightedSpecialLinkIds}
            seattleOverlay={seattleOverlay}
            placementError={placementError}
          />
        </div>
      </div>

      {/* 右側: サイドバー */}
      <div className={`w-[260px] md:w-[320px] lg:w-[360px] flex flex-col ${isMobile ? 'gap-0.5' : 'gap-1 md:gap-2'} flex-shrink-0`}>
        {/* PlayerInfoPanel（PCのみ表示 - スマホではヘッダーに統合） */}
        {!isMobile && (
          <div className="aos-card p-1.5 md:p-2">
            <div className="flex justify-between items-center mb-1 md:mb-2">
              <div className="flex items-center gap-1 md:gap-2">
                <span className={`font-heading text-sm md:text-base font-semibold ${playerStatsProps.player.isBankrupt ? 'text-destructive' : 'text-foreground'}`}>
                  {playerStatsProps.player.isBankrupt ? t.game.bankrupt : t.game.player}
                </span>
                {/* 選択アクション表示 */}
                {playerStatsProps.selectedActionName && (
                  <span className="bg-blue-100 text-blue-700 px-1 md:px-1.5 py-0.5 rounded text-[8px] md:text-[10px] font-bold">
                    {playerStatsProps.selectedActionName}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1 md:gap-2">
                <span className="text-[10px] md:text-xs text-muted-foreground">
                  {t.game.turn}: {playerStatsProps.maxTurns === -1 ? currentTurn : `${currentTurn}/${playerStatsProps.maxTurns}`}
                </span>
                {showGoodsDisplayButton && (
                  <button
                    className="btn-secondary text-[10px] md:text-xs px-1.5 py-0.5 md:px-2 md:py-1"
                    onClick={() => onSetShowGoodsDisplay(true)}
                  >
                    {t.stats.goods}
                  </button>
                )}
                <button
                  className="btn-ghost text-[10px] md:text-xs px-1 py-0.5 md:px-1.5 md:py-1"
                  onClick={() => setShowSettings(true)}
                  aria-label={t.settings.title}
                >
                  ⚙️
                </button>
              </div>
            </div>
            {/* ステータスグリッド（コンパクト）- 表示順序: Cash, Income, Expenses, Shares, Loco (, Cargo) */}
            <div className={`grid ${playerStatsProps.cargoTransportLevel !== undefined ? 'grid-cols-6' : 'grid-cols-5'} gap-0.5 md:gap-1 text-[10px] md:text-xs`}>
              <div className="flex flex-col items-center p-0.5 md:p-1 bg-muted/50 rounded">
                <span className="text-muted-foreground text-[8px] md:text-[10px]">{t.stats.cash}</span>
                <span className={`font-medium ${playerStatsProps.player.cash < 0 ? 'text-destructive' : ''}`}>
                  ${playerStatsProps.player.cash}
                </span>
              </div>
              <div className="flex flex-col items-center p-0.5 md:p-1 bg-muted/50 rounded">
                <span className="text-muted-foreground text-[8px] md:text-[10px]">{t.stats.income}</span>
                <span className={`font-medium ${playerStatsProps.player.incomeLevel <= 0 ? 'text-destructive' : ''}`}>
                  +{playerStatsProps.player.incomeLevel}
                  {/* 減額予定表示 */}
                  {playerStatsProps.incomeReduction !== undefined && playerStatsProps.incomeReduction > 0 && (
                    <span className="text-orange-500 text-[6px] md:text-[8px] ml-0.5">
                      (-{playerStatsProps.incomeReduction})
                    </span>
                  )}
                </span>
              </div>
              <div className="flex flex-col items-center p-0.5 md:p-1 bg-muted/50 rounded">
                <span className="text-muted-foreground text-[8px] md:text-[10px]">{t.stats.expenses}</span>
                <span className="font-medium text-destructive">
                  {playerStatsProps.expenses !== undefined ? `-$${playerStatsProps.expenses}` : '-'}
                </span>
              </div>
              <div className="flex flex-col items-center p-0.5 md:p-1 bg-muted/50 rounded">
                <span className="text-muted-foreground text-[8px] md:text-[10px]">{t.stats.shares}</span>
                <span className="font-medium">{playerStatsProps.player.sharesIssued}/{playerStatsProps.maxSharesLimit}</span>
              </div>
              <div className="flex flex-col items-center p-0.5 md:p-1 bg-muted/50 rounded">
                <span className="text-muted-foreground text-[8px] md:text-[10px]">{t.stats.locoLevel}</span>
                <span className="font-medium">{playerStatsProps.player.locomotiveLevel}/6</span>
              </div>
              {playerStatsProps.cargoTransportLevel !== undefined && (
                <div className="flex flex-col items-center p-0.5 md:p-1 bg-muted/50 rounded">
                  <span className="text-muted-foreground text-[8px] md:text-[10px]">{t.stats.cargoLevel}</span>
                  <span className="font-medium">{playerStatsProps.cargoTransportLevel}/{playerStatsProps.maxCargoTransportLevel ?? 5}</span>
                </div>
              )}
            </div>
            {/* 破産警告 */}
            {playerStatsProps.player.isBankrupt && (
              <div className="message-error text-center mt-1 md:mt-2 text-[10px] md:text-xs">
                <span className="font-semibold">{t.game.bankrupt} - {t.game.gameOver}</span>
              </div>
            )}
          </div>
        )}

        {/* Phase表示（コンパクト） */}
        <div className={`phase-header flex justify-between items-center ${isMobile ? 'py-0.5 px-1' : 'py-0.5 px-1.5 md:py-1 md:px-2'}`}>
          <h2 className={`${isMobile ? 'text-[18px]' : 'text-xs md:text-sm'} font-semibold truncate`}>{getPhaseDisplayName(phaseName)}</h2>
          {/* スマホ: 選択アクション表示（Phase名の横） */}
          {isMobile && playerStatsProps.selectedActionName && (
            <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-[14px] font-bold truncate max-w-[80px]">
              {playerStatsProps.selectedActionName}
            </span>
          )}
        </div>

        {/* フェーズ固有のコンテンツ */}
        <div className={`flex-1 aos-card ${isMobile ? 'overflow-y-auto' : 'overflow-hidden'}`}>{phaseContent}</div>
      </div>

      {/* 商品ディスプレイボードモーダル */}
      {showGoodsDisplay && (
        <div className="fixed inset-0 z-50">
          <GoodsDisplayBoard
            goodsDisplay={goodsDisplay}
            onClose={() => onSetShowGoodsDisplay(false)}
            productionMode={productionMode}
            hideDarkSide={mapName === 'Barbados'}
            rightAlignModal={mapName === 'Barbados' || mapName === 'Detroit Bankruptcy Solo'}
          />
        </div>
      )}

      {/* 設定モーダル */}
      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        onShowRules={onShowRules}
        onResetGame={onResetGame}
        onUndoToTurnStart={onUndoToTurnStart}
        canUndoToTurnStart={canUndoToTurnStart}
      />
    </div>
  );
}
